import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Loader2, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';
import type { GuildSummary } from '@/lib/api';

export function GuildInviteAcceptPage() {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const [guild, setGuild] = useState<GuildSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!inviteCode) return;
    let cancelled = false;
    setLoading(true);
    api
      .getGuildInviteInfo(inviteCode)
      .then((result) => {
        if (!cancelled) setGuild(result.guild);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Invalid invite');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [inviteCode]);

  const handleAccept = async () => {
    if (!inviteCode) return;
    setAccepting(true);
    setError(null);
    try {
      const member = await api.acceptGuildInvite(inviteCode);
      navigate(`/a/arche_guilds/${member.guildId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invite');
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-md mx-auto p-6 pt-20">
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  if (error && !guild) {
    return (
      <div className="max-w-md mx-auto p-6 pt-20">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Invalid Invite
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!guild) return null;

  return (
    <div className="max-w-md mx-auto p-6 pt-20">
      <Card>
        <CardHeader className="text-center">
          <CardTitle>You've been invited!</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <h2 className="text-xl font-semibold">{guild.name}</h2>
          {guild.description && (
            <p className="text-sm text-muted-foreground">{guild.description}</p>
          )}
          <div className="flex items-center justify-center text-sm text-muted-foreground">
            <Users className="h-4 w-4 mr-1" />
            {guild.memberCount} {guild.memberCount === 1 ? 'member' : 'members'}
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button onClick={handleAccept} disabled={accepting} className="w-full">
            {accepting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Accept Invite
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
